/**
 * Eine Zeile der C3-Aktivitäts-Sidebar.
 *
 * Badge (BEARBEITET/KI/SYNC/ERSTELLT) · Feld · Channel · Kurzbeschreibung ·
 * Zeitstempel · „Anzeigen" (im Editor öffnen) · „Wiederherstellen" (rollback).
 */

import { useTranslation } from 'react-i18next'
import { Pencil, Sparkles, RefreshCw, Plus } from 'lucide-react'
import { getBlockTypeInfo } from '@/hooks/useContentBlock'
import { useRollbackVersion } from '@/hooks/useVersionHistory'
import { EID } from '@/lib/element-ids.registry'
import type { ActivityBadge, ActivityEntry } from './activityFeed'

const BADGE_META: Record<ActivityBadge, { icon: typeof Pencil; cls: string }> = {
  edited: { icon: Pencil, cls: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300' },
  ai: { icon: Sparkles, cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  sync: { icon: RefreshCw, cls: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300' },
  created: { icon: Plus, cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
}

interface Props {
  entry: ActivityEntry
  onShow: (entry: ActivityEntry) => void
}

export function ActivityEntryRow({ entry, onShow }: Props) {
  const { t, i18n } = useTranslation()
  const rollback = useRollbackVersion('content_block', entry.blockId)
  const meta = BADGE_META[entry.badge]
  const Icon = meta.icon

  return (
    <li className="activity-entry rounded-md border border-neutral-200 dark:border-neutral-800 p-2.5 text-sm">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className={`activity-badge inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${meta.cls}`}>
          <Icon className="h-3 w-3" aria-hidden="true" />
          {t(`productContent.channels.activity.badge.${entry.badge}`)}
        </span>
        <span className="text-xs text-neutral-400">{new Date(entry.timestamp).toLocaleString(i18n.language)}</span>
      </div>
      <div className="text-neutral-700 dark:text-neutral-200">
        <span className="font-medium">{getBlockTypeInfo(entry.blockType).label}</span>
        <span className="text-neutral-400"> · {entry.channelName} · V{entry.versionNumber}</span>
      </div>
      {entry.summary && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">{entry.summary}</p>}
      <div className="flex items-center gap-3 mt-1.5">
        <button
          type="button"
          id={`${EID['content-activity-show-btn']}-${entry.id}`}
          className="text-primary-600 dark:text-primary-400 hover:underline text-xs"
          onClick={() => onShow(entry)}
        >
          {t('productContent.channels.activity.show')}
        </button>
        <button
          type="button"
          id={`${EID['content-activity-restore-btn']}-${entry.id}`}
          className="text-neutral-600 dark:text-neutral-300 hover:underline text-xs disabled:opacity-50"
          onClick={() => rollback.mutate(entry.versionId)}
          disabled={rollback.isPending}
        >
          {t('productContent.channels.activity.restore')}
        </button>
      </div>
    </li>
  )
}
